import { StorageItem, StorageResult, StorageOptions } from "./types";

const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

const validateKey = (key: string): boolean => {
  return typeof key === 'string' && key.length > 0;
};

const validateValue = <T>(value: T): boolean => {
  return value !== null && value !== undefined;
};

const validateTTL = (ttl?: number): boolean => {
  return ttl === undefined || (typeof ttl === 'number' && ttl > 0);
};

export const setStorageItem = <T>(
  key: string,
  value: T,
  options: StorageOptions = {}
): StorageResult<void> => {
  try {
    if (!validateKey(key)) {
      return { success: false, error: 'Invalid storage key' };
    }
    
    if (!validateValue(value)) {
      return { success: false, error: 'Invalid storage value' };
    }
    
    if (!validateTTL(options.ttl)) {
      return { success: false, error: 'Invalid TTL value' };
    }

    if (!isLocalStorageAvailable()) {
      return { success: false, error: 'LocalStorage is not available' };
    }

    const timestamp = Date.now();
    const expiresAt = options.ttl ? timestamp + options.ttl : undefined;

    const storageItem: StorageItem<T> = {
      value,
      timestamp,
      expiresAt
    };

    localStorage.setItem(key, JSON.stringify(storageItem));
    
    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
};

export const getStorageItem = <T>(key: string): StorageResult<T> => {
  try {
    if (!validateKey(key)) {
      return { success: false, error: 'Invalid storage key' };
    }

    if (!isLocalStorageAvailable()) {
      return { success: false, error: 'LocalStorage is not available' };
    }

    const item = localStorage.getItem(key);
    
    if (!item) {
      return { success: false, error: 'Item not found' };
    }

    let storageItem: StorageItem<T>;
    
    try {
      storageItem = JSON.parse(item);
    } catch {
      return { success: false, error: 'Invalid storage item format' };
    }

    if (!storageItem || typeof storageItem !== 'object' || !('value' in storageItem) || !('timestamp' in storageItem)) {
      return { success: false, error: 'Invalid storage item structure' };
    }

    if (storageItem.expiresAt && Date.now() > storageItem.expiresAt) {
      localStorage.removeItem(key);
      return { success: false, error: 'Item has expired' };
    }

    return { success: true, data: storageItem.value };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
};

export const removeStorageItem = (key: string): StorageResult<void> => {
  try {
    if (!validateKey(key)) {
      return { success: false, error: 'Invalid storage key' };
    }

    if (!isLocalStorageAvailable()) {
      return { success: false, error: 'LocalStorage is not available' };
    }

    localStorage.removeItem(key);
    
    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
};

export const cleanupExpiredItems = (): StorageResult<number> => {
  try {
    if (!isLocalStorageAvailable()) {
      return { success: false, error: 'LocalStorage is not available' };
    }

    let removedCount = 0;
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      const item = localStorage.getItem(key);
      
      if (item) {
        try {
          const storageItem: StorageItem = JSON.parse(item);
          
          if (storageItem.expiresAt && Date.now() > storageItem.expiresAt) {
            localStorage.removeItem(key);
            removedCount++;
          }
        } catch {
          continue;
        }
      }
    }

    return { success: true, data: removedCount };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}; 